import gsap from "gsap";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(SplitText);

export function createIntroController({
  enabled,
  animateCameraToInitial,
  onComplete,
  targetDocument = document,
}) {
  const root = targetDocument.querySelector(".intro");
  const loader = targetDocument.querySelector(".intro__loader");
  const content = targetDocument.querySelector(".intro__content");
  const progress = targetDocument.querySelector(".intro__progress");
  const progressValue = targetDocument.querySelector(
    ".intro__progress-value",
  );
  const loading = targetDocument.querySelector(".intro__loading");
  const meta = targetDocument.querySelector(".intro__meta");
  const title = targetDocument.querySelector(".intro__title");
  const description = targetDocument.querySelector(".intro__description");
  const button = targetDocument.querySelector(".intro__button");
  const reducedMotion = targetDocument.defaultView?.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const progressState = { value: 0 };
  let dismissed = !enabled;
  let completed = false;
  let introTimeline = null;
  let exitTimeline = null;
  let splitTitle = null;
  let destroyed = false;

  function completeOnce() {
    if (completed) return;
    completed = true;
    onComplete();
  }

  function dismiss() {
    if (dismissed) return;
    dismissed = true;

    const cameraAnimationStarted = animateCameraToInitial(completeOnce);
    const exitTargets = [meta, title, description, button].filter(Boolean);

    if (reducedMotion) {
      root?.remove();
      if (!cameraAnimationStarted) completeOnce();
      return;
    }

    exitTimeline = gsap
      .timeline({
        onComplete: () => {
          root?.remove();
          if (!cameraAnimationStarted) completeOnce();
        },
      })
      .to(exitTargets, {
        duration: 0.5,
        autoAlpha: 0,
        y: -20,
        stagger: 0.035,
        ease: "power3.in",
      })
      .to(
        root,
        {
          duration: 1.05,
          clipPath: "inset(0 0 100% 0)",
          ease: "power4.inOut",
        },
        0.08,
      );
  }

  button?.addEventListener("click", dismiss);

  return {
    start() {
      if (!enabled || !root) {
        dismissed = true;
        root?.remove();
        completeOnce();
        return;
      }

      const fontsReady = targetDocument.fonts?.ready ?? Promise.resolve();
      fontsReady.then(() => {
        if (destroyed) return;

        if (reducedMotion) {
          if (progressValue) progressValue.textContent = "100";
          gsap.set(root, { autoAlpha: 1 });
          gsap.set(loader, { display: "none" });
          gsap.set(content, { autoAlpha: 1 });
          gsap.set([meta, description, button].filter(Boolean), {
            autoAlpha: 1,
            y: 0,
          });
          return;
        }

        if (title) {
          splitTitle = new SplitText(title, {
            type: "lines",
            linesClass: "intro__line",
            mask: "lines",
          });
        }

        introTimeline = gsap.timeline({ defaults: { ease: "power3.out" } });
        introTimeline
          .set(root, { autoAlpha: 1 })
          .to(
            [progress, loading].filter(Boolean),
            {
              duration: 0.8,
              autoAlpha: 1,
              y: 0,
              stagger: 0.08,
            },
            0.1,
          )
          .to(
            progressState,
            {
              duration: 1.55,
              value: 100,
              ease: "power2.inOut",
              onUpdate: () => {
                if (progressValue) {
                  progressValue.textContent = String(
                    Math.round(progressState.value),
                  );
                }
              },
            },
            0.5,
          )
          .to(
            [progress, loading].filter(Boolean),
            {
              duration: 0.6,
              autoAlpha: 0,
              yPercent: -115,
              stagger: 0.06,
              ease: "power3.in",
            },
            ">+0.16",
          )
          .set(loader, { display: "none" })
          .set(content, { autoAlpha: 1 })
          .addLabel("contentReveal");

        if (splitTitle) {
          introTimeline.fromTo(
            splitTitle.lines,
            { yPercent: 120 },
            {
              duration: 1.05,
              yPercent: 0,
              stagger: 0.12,
              ease: "power4.out",
            },
            "contentReveal+=0.08",
          );
        }

        introTimeline
          .to(
            [meta].filter(Boolean),
            { duration: 0.7, autoAlpha: 1, y: 0 },
            "contentReveal+=0.18",
          )
          .to(
            [description, button].filter(Boolean),
            {
              duration: 0.75,
              autoAlpha: 1,
              y: 0,
              stagger: 0.08,
            },
            "contentReveal+=0.65",
          );
      });
    },
    dismiss,
    isDismissed: () => dismissed,
    destroy() {
      destroyed = true;
      button?.removeEventListener("click", dismiss);
      introTimeline?.kill();
      exitTimeline?.kill();
      splitTitle?.revert();
    },
  };
}
